/* eslint-disable camelcase */
import { node, icon } from "~/util/dom";
import { ADD_FIRM_LINK, ERROR } from "~/report/const";
import Report from "~/report/index";
import { getAddPlaceOptions } from "~/report/options";
import { IInputType } from "~/report/form/input";
import { IReportData, TSendAction, IAddressItem, IMantisData, TProject, IAttachment, TDrawData, TNoteData, TTransform, IGetSendDataOutput } from "./interfaces";
import { store } from "~/state/store";
import { CONFIG } from "~/config";
import { _ } from "~/i18n";
import { isPointInStatePolygon } from "~/util/state-polygon";
import { isPanoSupported } from "~/util/providers/pano";
import { ELoginState } from "~/state/user/interfaces";

export function reportWaitForLogin() {
	return new Promise(resolve => {
		const { user } = store.getState().user;

		if (user.state === ELoginState.Green) {
			resolve({});
		} else {
			Mapy.getComponent("login").open({
				analyticsData: {
					loginOrigin: "errorReportForm",
				},
				motivation: "report",
			}).then(() => {
				resolve({});
			});
		}
	});
}

export function getDetailItems(includeIco?: boolean): Array<{
	name: string;
	iconName: string;
	placeholder: string;
	type: IInputType;
}> {
	// TODO validace - pridat pattern?
	return [
		{ name: "www", iconName: "globe", placeholder: _("search.visualizer.link2"), type: "text" },
		{ name: "phone", iconName: "phone", placeholder: _("mobilePromo.form.phone"), type: "text" },
		{ name: "email", iconName: "envelope", placeholder: _("report.email"), type: "email" },
		...includeIco
			? [{ name: "ico", iconName: "report-store", placeholder: _("report.ico"), type: "text" as IInputType }]
			: [],
	];
}

export function getReportConfirmOptions(): { text: string; confirmText: string; rejectText: string; } {
	return {
		text: _("report.cancelFilledData"),
		confirmText: _("detail.content.review2.throwAway"),
		rejectText: _("back"),
	};
}

export function createThankYou(): HTMLDivElement {
	const cover = node("div", { className: "report-thankyou-cover" });
	const container = node("div", { className: "report-thankyou" });
	const iconElem = icon("check-circle");
	const msg = node("p", { innerHTML: _("report.thankYou", { pageBreak: "<br />" }) });

	cover.append(
		container,
	);
	container.append(
		iconElem,
		msg,
	);

	return cover;
}

export function getMapAddressData(mapData: any): IAddressItem {
	const { source, id, coords, label } = mapData;
	const isKnownAddress = ["addr", "coor", "stre"].includes(source);

	return {
		id,
		source,
		longitude: coords.x,
		latitude: coords.y,
		isKnownAddress,
		zoom: 0,
		locationObj: {
			lat: coords.y,
			lon: coords.x,
			address: source === "coor" ? `${coords.y},${coords.x}` : label,
			selectedFromMap: true,
		},
	};
}

export function getSuggestAddressData(suggestData: any): IAddressItem {
	const { source, id, longitude, latitude, secondRow, title } = suggestData;

	return {
		id,
		source,
		longitude,
		latitude,
		isKnownAddress: true,
		zoom: Mapy.Util.computeZoom(suggestData),
		locationObj: {
			lat: latitude,
			lon: longitude,
			address: `${title}, ${secondRow}`,
			selectedFromMap: false,
		},
	};
}

export function getSuggestCategoryData({ source, id, title, poiTypeId }) {
	return { id, source, title, poiTypeId };
}

export async function addFirmWithDestination(isCzechia: boolean, analytics: Partial<IReportData>, values: any = {}, coords?: any): Promise<void> {
	// v cesku link, mimo dialog
	if (isCzechia) {
		window.open(ADD_FIRM_LINK);
	} else {
		await reportWaitForLogin();

		const curState = store.getState();
		const report = new Report(getAddPlaceOptions({
			values,
			analytics,
			coords: coords || {
				lon: curState.map.longitude,
				lat: curState.map.latitude,
			},
		}));

		report.open();
	}
}

export function createDetailItem(iconName: string, caption: string, onClick: () => void): HTMLDivElement {
	const container = node("div", { className: "contact-report-item" });
	const button = node("button", {
		className: "contact-report-itemButton",
		textContent: caption,
	});

	container.append(
		icon(iconName),
		button,
	);
	button.setAttribute("data-log-ignore", "");
	button.addEventListener("click", () => {
		onClick();
	});

	return container;
}

export function errorToMsg(error: number): string {
	switch (error) {
		case ERROR.ERROR:
			return _("imageUploader2.errorTitle");
		case ERROR.EXT:
			return _("firmPhotos.error.extension");
		case ERROR.SIZE:
			return _("firmPhotos.error.size");
		case ERROR.MAX_PHOTOS:
			return _("firmPhotos.error.maxCount");
		case ERROR.UPLOAD:
			return _("firmPhotos.error.photoUpload");
		default:
			return "";
	}
}

export function reportAnalytics(data: IReportData, action: TSendAction = "error_report") {
	Mapy.getComponent("logger").log(action, {
		...data,
	});
}

interface IGetSendData {
	state: any;
	project: TProject;
	rusId?: number;
	mediaIds?: Array<number>;
	attachments?: Array<IAttachment>;
	drawCoords?: TDrawData;
	transformData?: TTransform;
}

export async function getSendData({
	state = {},
	project = "",
	rusId = 0,
	mediaIds = [],
	attachments = [],
	transformData = (noteData: TNoteData, mantisData: IMantisData) => ({
		note: JSON.stringify(noteData, null, "\t"),
		noteData,
		mantisData,
	}),
}: IGetSendData): Promise<IGetSendDataOutput> {
	const noteData = {
		...state,
		...mediaIds.length
			? {
				mediaIds,
			}
			: {},
	};
	const { zoom } = store.getState().map;
	const mantisData: IMantisData = {
		email: state["user-email"] || "",
		project: project,
		rusId: rusId,
		url: `${location.pathname}${location.search}`,
		attachments,
		zoom,
		webGL: isPanoSupported(),
		language: CONFIG.defaultLang,
		mapSet: store.getState().map.mapset,
		host: location.host,
		// cmaranice
		data: state.drawUtil || [],
	};

	const hints = {};
	let isCzechia = false;

	attachments.forEach((data, ind) => {
		hints[`1.attachments.${ind}.content`] = "binary";
	});

	// prepiseme na celou adresu
	if (noteData.address && noteData["address-data"] && Object.keys(noteData["address-data"].locationObj || {}).length > 0) {
		noteData.location = noteData["address-data"].locationObj;
	}

	if (noteData["address-data"]) {
		isCzechia = await isPointInStatePolygon(noteData["address-data"].longitude, noteData["address-data"].latitude, {
			cs: true,
		});
		delete noteData["address-data"];
	}

	// smazeme klice z poznamky
	[
		"attachment",
		"category-data",
		"user-email",
		"photos",
		"address",
	].forEach(name => name in noteData && delete noteData[name]);

	return {
		hints,
		isCzechia,
		...transformData(noteData, mantisData),
	};
}

export function parseAttachments(attachments: Array<IAttachment>): string {
	const files = attachments.filter(item => !item.screenshot);

	if (files.length) {
		const hasImageFile = files.filter(item => Mapy.ImageUploader.MIME_TYPES.indexOf(item.mime) !== -1).length > 0;

		if (hasImageFile) {
			return "obsahuje vloženou fotografii.";
		}

		return "obsahuje vložený soubor.";
	}

	return "";
}
